import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const WHATSAPP_NUMBER = "+2348030914767";

const projectTypes = [
  "Web App",
  "Mobile App",
  "API / Backend / Microservices",
  "MVP / Technical Consulting",
  "Other",
];

const budgetRanges = [
  "Not sure yet",
  "Under $5,000",
  "$5,000 – $15,000",
  "$15,000 – $50,000",
  "$50,000+",
];

const initialForm = {
  name: "",
  email: "",
  projectType: projectTypes[0],
  budget: budgetRanges[0],
  details: "",
};

const buildWhatsAppMessage = ({ name, email, projectType, budget, details }) =>
  `Hi Codehouze, I'd like to start a project.\n\n` +
  `Name: ${name}\n` +
  `Email: ${email}\n` +
  `Project type: ${projectType}\n` +
  `Budget: ${budget}\n\n` +
  `Details:\n${details}`;

const StartProjectModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    setForm(initialForm);
    setErrors({});
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required.";
    if (!form.email.trim()) nextErrors.email = "Email is required.";
    if (!form.details.trim()) nextErrors.details = "Tell us a bit about the project.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const message = buildWhatsAppMessage(form);
    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
      "noopener,noreferrer"
    );
    handleClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="relative bg-gray-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 pb-0">
            <h3 className="text-white text-xl font-bold">Start a Project</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 p-2 rounded-lg transition-colors"
              aria-label="Close form"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-4">
            <p className="text-gray-500 text-sm">
              Tell us a bit about what you're building. We'll open WhatsApp with your
              details ready to send.
            </p>

            <div>
              <label htmlFor="name" className="block text-sm text-gray-300 mb-1.5">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Jane Doe"
                className="w-full bg-white/5 border border-white/10 focus:border-codehouzeOrange/60 rounded-lg px-3.5 py-2.5 text-white placeholder:text-gray-600 text-sm outline-none transition-colors"
              />
              {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm text-gray-300 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                placeholder="jane@company.com"
                className="w-full bg-white/5 border border-white/10 focus:border-codehouzeOrange/60 rounded-lg px-3.5 py-2.5 text-white placeholder:text-gray-600 text-sm outline-none transition-colors"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="projectType" className="block text-sm text-gray-300 mb-1.5">
                  Project type
                </label>
                <select
                  id="projectType"
                  value={form.projectType}
                  onChange={handleChange("projectType")}
                  className="w-full bg-white/5 border border-white/10 focus:border-codehouzeOrange/60 rounded-lg px-3.5 py-2.5 text-white text-sm outline-none transition-colors"
                >
                  {projectTypes.map((type) => (
                    <option key={type} value={type} className="bg-gray-900">
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm text-gray-300 mb-1.5">
                  Budget
                </label>
                <select
                  id="budget"
                  value={form.budget}
                  onChange={handleChange("budget")}
                  className="w-full bg-white/5 border border-white/10 focus:border-codehouzeOrange/60 rounded-lg px-3.5 py-2.5 text-white text-sm outline-none transition-colors"
                >
                  {budgetRanges.map((range) => (
                    <option key={range} value={range} className="bg-gray-900">
                      {range}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="details" className="block text-sm text-gray-300 mb-1.5">
                Project details
              </label>
              <textarea
                id="details"
                rows={4}
                value={form.details}
                onChange={handleChange("details")}
                placeholder="What are you building, and what do you need help with?"
                className="w-full bg-white/5 border border-white/10 focus:border-codehouzeOrange/60 rounded-lg px-3.5 py-2.5 text-white placeholder:text-gray-600 text-sm outline-none transition-colors resize-none"
              />
              {errors.details && (
                <p className="text-red-400 text-xs mt-1">{errors.details}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20b858] text-white font-semibold py-3 rounded-xl transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
              Continue on WhatsApp
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StartProjectModal;
